import React, { Component } from "react";
import { findDOMNode } from "react-dom";
import { getEmptyImage } from "react-dnd-html5-backend";
import { DragSource } from "react-dnd";
import { getHtmlNodeCenter } from "lib/htmlElements/dimensions";
import { rotatePoint } from "lib/geometry/rotation";
import { getCanvasbackground } from "views/components/Editor/utils";
import style from "views/components/Editor/styleSelectionBox.module.css";

const ImageCroppingHandlerSource = {
  beginDrag(props, monitor, component) {
    props.startCropMaskResize();

    const componentNode = findDOMNode(component);
    const selectionBoxNode = componentNode.parentElement;

    const componentNodeCenter = getHtmlNodeCenter(componentNode);
    const selectionBoxNodeCenter = getHtmlNodeCenter(selectionBoxNode);

    const originalHandlerPosition = rotatePoint(
      componentNodeCenter.x,
      componentNodeCenter.y,
      selectionBoxNodeCenter.x,
      selectionBoxNodeCenter.y,
      -props.imageElement.angle
    );

    const canvasBackground = getCanvasbackground();

    const pageDOMNode = canvasBackground.querySelector(
      `[id="PAGE-${props.pageId}"]`
    );
    const elementDOMNode = pageDOMNode.querySelector(
      `[id="${props.imageElement.uniqueId}"]`
    ).firstChild;
    const elementRect = elementDOMNode.getBoundingClientRect();

    return {
      rotatedHandlerPosition: componentNodeCenter,
      selectionBoxNodeCenter,
      originalHandlerPosition: originalHandlerPosition,
      position: props.position,
      component,
      originalElementRect: elementRect
    };
  },

  endDrag(props, monitor, component) {
    const differenceFromInitialOffset =
      monitor.getDifferenceFromInitialOffset();

    const dragItem = monitor.getItem();
    const { originalHandlerPosition, originalElementRect } = dragItem;

    const componentNode = findDOMNode(component);
    if (!componentNode) {
      // Touch-backend race: component unmounted between drop() and endDrag().
      // Close the crop-resize lifecycle that beginDrag opened, with zero
      // offsets, so Redux is not left mid-crop-resize.
      props.finishCropMaskResize({
        differenceFromInitialOffset: { x: 0, y: 0 },
        dragItem,
        adjustmentOffset: { x: 0, y: 0 }
      });
      return {};
    }
    const selectionBoxNode = componentNode.parentElement;

    const componentNodeCenter = getHtmlNodeCenter(componentNode);
    const selectionBoxNodeCenter = getHtmlNodeCenter(selectionBoxNode);

    // can get the point the same distance away with the given negative rotation
    const newPoint = rotatePoint(
      componentNodeCenter.x + differenceFromInitialOffset.x, // new point is initial point plus difference
      componentNodeCenter.y + differenceFromInitialOffset.y,
      selectionBoxNodeCenter.x,
      selectionBoxNodeCenter.y,
      -props.imageElement.angle
    );

    // the offset after rotating is the rotated offset minus the original point
    const rotatedOffset = {
      x: newPoint.x - originalHandlerPosition.x,
      y: newPoint.y - originalHandlerPosition.y
    };

    let adjustmentOffset = { x: 0, y: 0 };

    const updatedElementDOMNode = document.querySelector(
      `#preview-${props.imageElement.uniqueId} > div > div`
    );

    if (updatedElementDOMNode) {
      const updatedElementRect = updatedElementDOMNode.getBoundingClientRect();

      adjustmentOffset = {
        x: originalElementRect.top - updatedElementRect.top,
        y: originalElementRect.left - updatedElementRect.left
      };
    }

    props.finishCropMaskResize({
      differenceFromInitialOffset: rotatedOffset,
      dragItem: monitor.getItem(),
      adjustmentOffset
    });

    return {};
  }
};

/**
 * Specifies the props to inject into your component.
 */
function collect(connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
    differenceFromInitialOffset: monitor.getDifferenceFromInitialOffset()
  };
}

class ImageCroppingHandler extends Component {
  componentDidMount() {
    const { connectDragPreview } = this.props;

    if (connectDragPreview) {
      connectDragPreview(getEmptyImage(), {
        captureDraggingState: true
      });
    }
  }

  render() {
    const { connectDragSource, imageElement, position, zoom } = this.props;
    const cropHandleMinimumDimension = 28;
    const isCropBelowMinHeight =
      imageElement.height * zoom < cropHandleMinimumDimension;
    const isCropBelowMinWidth =
      imageElement.width * zoom < cropHandleMinimumDimension;

    return connectDragSource(
      <div
        className={style.croppingDot}
        data-position={position}
        data-is-crop={true}
        data-min-height={isCropBelowMinHeight}
        data-min-width={isCropBelowMinWidth}
        data-x-handles={true}
        data-y-handles={true}
      >
        <div className={style.lowerHandles}>
          <div className={style.verticalHandle} />
          <div className={style.horizontalHandle} />
        </div>
        <div className={style.upperHandles}>
          <div className={style.verticalHandle} />
          <div className={style.horizontalHandle} />
        </div>
      </div>
    );
  }
}

const ImageCroppingHandlerDraggable = DragSource(
  "IMAGE_CROP_HANDLER",
  ImageCroppingHandlerSource,
  collect
)(ImageCroppingHandler);

const ImageCroppingHandlerDefault = props => {
  return (
    <ImageCroppingHandlerDraggable
      {...props}
      canvasBackgroundHtmlElement={props.canvasBackground}
      startCropResizing={props.startImageCropResizing}
      finishCropResizing={props.finishImageCropResizing}
      selectedItems={props.selectedItems}
      getElement={props.designData.getElement.bind(props.designData)}
      zoom={props.zoom}
    />
  );
};

export default ImageCroppingHandlerDefault;
